// ════════════════════════════════════════════════════════════════════
// Escucha global de jornadas BAC
// ════════════════════════════════════════════════════════════════════
// Mientras la app esté abierta (y con sesión), revisa cada minuto si en
// el servidor hay jornadas nuevas o cambiadas, en TODOS los territorios
// y comunidades — no hace falta haber entrado a esa comunidad.
//
// Para no cargar el servidor, cada minuto solo pide la "firma"
// (cantidad + última modificación). Únicamente si la firma cambió
// descarga la lista completa, la guarda en el dispositivo y avisa de
// las novedades (evento "bac-jornadas-novedades").
//
// También revisa al instante: al abrir la app, al volver a ella desde
// otra app, al recuperar la señal y al terminar una sincronización.
// ════════════════════════════════════════════════════════════════════
import { useEffect } from "react";
import { apiFirmaJornadas, precargarJornadasTodas } from "./jornadasBac";

const INTERVALO_MS = 60 * 1000;
const RESPALDO_MS = 5 * 60 * 1000; // si el servidor aún no tiene /firma
const CLAVE_FIRMA = "bac_jornadas_firma";

const leer = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const guardar = (k, v) => { try { localStorage.setItem(k, v); } catch { /* sin almacenamiento */ } };

let servidorSinFirma = false;
let ultimaListaCompleta = 0;
let revisando = false;

async function revisar(forzar = false) {
  if (revisando || !navigator.onLine) return;
  if (!forzar && document.visibilityState === "hidden") return;
  revisando = true;
  try {
    if (!servidorSinFirma) {
      try {
        const { data } = await apiFirmaJornadas();
        const firma = `${data?.total}|${data?.ultima}`;
        if (!forzar && leer(CLAVE_FIRMA) === firma) return; // nada nuevo
        const r = await precargarJornadasTodas();
        if (r !== null) guardar(CLAVE_FIRMA, firma);
        return;
      } catch (e) {
        // 404 = el servidor todavía no tiene /firma → plan B más abajo.
        // Cualquier otro error (sin señal, timeout): se reintenta en 1 min.
        if (e?.response?.status === 404) servidorSinFirma = true;
        else return;
      }
    }
    // Plan B: lista completa, como máximo cada 5 minutos
    if (forzar || Date.now() - ultimaListaCompleta > RESPALDO_MS) {
      ultimaListaCompleta = Date.now();
      await precargarJornadasTodas();
    }
  } finally {
    revisando = false;
  }
}

export default function useEscuchaJornadas(activo) {
  useEffect(() => {
    if (!activo) return;
    revisar(true);
    const reloj = setInterval(() => revisar(), INTERVALO_MS);
    const forzar = () => revisar(true);
    const alVolver = () => { if (document.visibilityState === "visible") revisar(true); };
    window.addEventListener("online", forzar);
    window.addEventListener("sync-completado", forzar);
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      clearInterval(reloj);
      window.removeEventListener("online", forzar);
      window.removeEventListener("sync-completado", forzar);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [activo]);
}
